import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { CalendarClock, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useCan } from '@/lib/permissions';

const TYPE_LABEL = {
  rate_change: 'Rate change', reinsurance_change: 'Reinsurance change',
  reserve_strengthening: 'Reserve strengthening', underwriting_change: 'Underwriting change',
  product_launch: 'Product launch', catastrophe: 'Catastrophe', regulatory: 'Regulatory', other: 'Other',
};

const empty = () => ({ name: '', description: '', event_type: 'rate_change', event_date: '', end_date: '', model_id: '', applies_to: [], expected_direction: 'uncertain', affected_metrics: [], related_goal: '', related_initiative: '' });

export default function BusinessEventsPage() {
  const canWrite = useCan('measures:certify');
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [goals, setGoals] = useState([]);
  const [options, setOptions] = useState({ event_types: [], directions: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listBusinessEvents();
      setItems(r.events || []);
    } catch (e) { toast.error(e.message || 'Could not load events'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.getBusinessEventOptions().then(setOptions).catch(() => {});
    api.getSemanticModels().then((r) => setModels(r.models || r || [])).catch(() => {});
    api.listObjectives().then((r) => setGoals((r.objectives || []).filter((o) => o.kind !== 'guardrail'))).catch(() => {});
  }, [load]);

  const modelName = (id) => (models.find((m) => m.model_id === id) || {}).name || id;

  const openNew = () => { setEditing(null); setForm(empty()); setDialogOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name || '', description: o.description || '', event_type: o.event_type || 'other',
      event_date: o.event_date || '', end_date: o.end_date || '', model_id: o.model_id || '', applies_to: o.applies_to || [],
      expected_direction: o.expected_direction || 'uncertain', affected_metrics: o.affected_metrics || [],
      related_goal: o.related_goal || '', related_initiative: o.related_initiative || '' });
    setDialogOpen(true);
  };
  const toggleCat = (c) => setForm((p) => ({ ...p, applies_to: p.applies_to.includes(c) ? p.applies_to.filter((x) => x !== c) : [...p.applies_to, c] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('A name is required'); return; }
    if (!form.event_date) { toast.error('An event date is required'); return; }
    setSaving(true);
    try {
      if (editing) await api.updateBusinessEvent(editing.event_id, form);
      else await api.createBusinessEvent(form);
      toast.success(editing ? 'Event updated' : 'Event logged');
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message || 'Could not save'); }
    finally { setSaving(false); }
  };

  const remove = async (o) => {
    try { await api.deleteBusinessEvent(o.event_id); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.message || 'Could not remove'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="business-events-page">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarClock className="w-6 h-6 text-violet-500" /> Business Events</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            A dated ledger of business events — rate changes, reinsurance renewals, reserve strengthening,
            catastrophes and more. When a recommendation's outcome is measured, events in the measurement
            window are surfaced as context, so you can tell a real result from a confounder.
          </p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid="event-new-btn"><Plus className="w-4 h-4 mr-1" /> Log event</Button>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="events-empty">
          No business events yet. Log the changes your team makes (e.g. "Filed a +6% rate change on Auto") so recommendation outcomes can be interpreted in context.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="events-list">
          {items.map((o) => (
            <div key={o.event_id} className="bg-card/60 border border-border/40 rounded-2xl p-4" data-testid={`event-card-${o.event_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-heading font-semibold text-foreground flex items-center gap-1.5">
                  <CalendarClock className="w-4 h-4 text-violet-500" /> {o.name}
                </div>
                {canWrite && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`event-edit-${o.event_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(o)} data-testid={`event-delete-${o.event_id}`}><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
                  </div>
                )}
              </div>
              {o.description && <p className="text-xs text-muted-foreground mt-1">{o.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                <span className="px-1.5 py-0.5 rounded-md border border-violet-500/30 bg-violet-500/10 text-violet-600">{TYPE_LABEL[o.event_type] || o.event_type}</span>
                <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">{o.event_date}</span>
                {o.model_id && <span className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground">{modelName(o.model_id)}</span>}
                {(o.applies_to || []).map((c) => <span key={c} className="px-1.5 py-0.5 rounded-md border border-border/40 text-muted-foreground capitalize">{c}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Log'} business event</DialogTitle>
            <DialogDescription>Recorded for context when recommendation outcomes are measured.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Filed +6% Auto rate change" data-testid="event-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional" data-testid="event-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select value={form.event_type} onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="event-type">
                  {(options.event_types || []).map((k) => <option key={k} value={k}>{TYPE_LABEL[k] || k}</option>)}
                </select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setForm((p) => ({ ...p, event_date: e.target.value }))} data-testid="event-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>End date (optional)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} data-testid="event-end-date" />
              </div>
              <div>
                <Label>Expected direction</Label>
                <select value={form.expected_direction} onChange={(e) => setForm((p) => ({ ...p, expected_direction: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="event-direction">
                  {(options.directions || ['positive', 'negative', 'uncertain']).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Affected metrics (comma-separated)</Label>
              <Input value={(form.affected_metrics || []).join(', ')} onChange={(e) => setForm((p) => ({ ...p, affected_metrics: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))} placeholder="e.g. Loss ratio, Combined ratio" data-testid="event-metrics" />
            </div>
            <div>
              <Label>Related goal (optional)</Label>
              <select value={form.related_goal} onChange={(e) => setForm((p) => ({ ...p, related_goal: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="event-related-goal">
                <option value="">None</option>
                {goals.map((g) => <option key={g.objective_id} value={g.objective_id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Model (optional)</Label>
              <select value={form.model_id} onChange={(e) => setForm((p) => ({ ...p, model_id: e.target.value }))} className="h-9 w-full rounded-md border border-border/50 bg-background px-2 text-sm mt-1" data-testid="event-model">
                <option value="">All models</option>
                {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
              </select>
            </div>
            <div>
              <Label>Applies to business areas</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="event-applies">
                {(options.categories || []).map((c) => {
                  const on = form.applies_to.includes(c);
                  return (
                    <button key={c} onClick={() => toggleCat(c)} className={`text-[11px] px-2 py-1 rounded-full border capitalize transition-colors ${on ? 'bg-primary/15 border-primary/40 text-primary' : 'border-border/40 text-muted-foreground hover:bg-muted/40'}`} data-testid={`event-cat-${c}`}>{c}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="event-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
